/** @jsx isolateComponent */

import { AsyncIndicator, FlexLayout } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { Button } from '@zapier/design-system';
import { Price } from '@zapier/currency';

import { useSize } from '@zapier/design-system-revenue/src/utils';

import { useData as _useData } from './hooks/useData';

import {
  Props as ShouldRenderProps,
  useShouldRender,
} from './hooks/useShouldRender';

type Props = {
  /** For dependency injection testing only */
  useData?: () => ReturnType<typeof _useData>;
};

const buttonColorThemeBase = {
  backgroundColor: 'transparent',
};

const buttonColorTheme = {
  custom: {
    base: buttonColorThemeBase,
    disabled: buttonColorThemeBase,
    hover: buttonColorThemeBase,
    focus: buttonColorThemeBase,
  },
};

const TeamTrialBanner = ({ useData = _useData }: Props = {}) => {
  const {
    billingInterval,
    isLoading,
    shouldAutoUpgrade,
    shouldRenderToggle,
    teamPlanMonthlyAmount,
    toggleTeamAutoUpgrade,
    trialDaysRemaining,
  } = useData();

  const size = useSize();

  const autoUpgradeText = shouldAutoUpgrade
    ? 'Disable Auto-Upgrade'
    : 'Enable Auto-Upgrade';

  const toggle = shouldRenderToggle && (
    <Button
      color="custom"
      colorTheme={buttonColorTheme}
      isPadded={false}
      onClick={() => toggleTeamAutoUpgrade()}
      size="xsmall"
    >
      {autoUpgradeText}
    </Button>
  );

  return isLoading ? (
    <AsyncIndicator />
  ) : (
    <FlexLayout
      align="center"
      justify={size === 'large' ? 'space-between' : 'center'}
    >
      <span>
        You’re on a free trial of Zapier for Teams and have {trialDaysRemaining}{' '}
        days left in your trial. After that you will{' '}
        {shouldAutoUpgrade ? (
          <span>
            be automatically upgraded and charged{' '}
            <Price>{teamPlanMonthlyAmount}</Price>
            {`/mo ${
              billingInterval === 'year' ? '(paid annually)' : ''
            }`.trim()}
          </span>
        ) : (
          'lose access to Team features'
        )}
        .
      </span>

      {toggle}
    </FlexLayout>
  );
};

TeamTrialBanner.shouldRender = function shouldRender(
  props: ShouldRenderProps = {}
) {
  return useShouldRender.call(this, props);
};

export { TeamTrialBanner };
